"use client";

import React from "react";
import { HelpCircle } from "lucide-react";
import { ResponsivePopover } from "@/components/ui/responsive-popover";
import { cn } from "@/lib/utils";
import { getFieldHelp, getEntityFieldHelp } from "@/lib/field-help";
import type { FieldHelp } from "@/lib/field-help";

// A "?" help affordance for CRM fields. Renders a muted question-mark icon that
// opens a ResponsivePopover — on desktop a portal-rendered popover (Mantine,
// portalled to document.body → still immune to table/modal overflow clip, per
// Z_INDEX_AND_OVERFLOW), on mobile a drawer — with a header + paragraph +
// optional usage/related notes. Renders NOTHING when no help is defined for the
// field, so it's safe to drop next to every label unconditionally.

interface HelpIconButtonProps {
  /** Accessible label for the trigger. */
  ariaLabel: string;
  className?: string;
}

/** The shared muted "?" trigger button. Colors only on hover/focus. */
const HelpIconButton = React.forwardRef<HTMLButtonElement, HelpIconButtonProps & React.ComponentPropsWithoutRef<"button">>(
  function HelpIconButton({ ariaLabel, className, ...props }, ref) {
    return (
      <button
        ref={ref}
        type="button"
        aria-label={ariaLabel}
        onClick={(e) => {
          // Don't let the click bubble to a sortable header / clickable card.
          e.stopPropagation();
          props.onClick?.(e);
        }}
        className={cn(
          "inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-muted-foreground/50 transition-colors hover:text-primary focus-visible:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          className
        )}
        {...props}
      >
        <HelpCircle className="h-3.5 w-3.5" />
      </button>
    );
  }
);

/** Rendered body of a single field's help. */
function FieldHelpBody({ help }: { help: FieldHelp }) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-semibold text-foreground">{help.title}</p>
      <p className="text-sm text-muted-foreground">{help.description}</p>
      {help.usage && (
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground/80">How it's used: </span>
          {help.usage}
        </p>
      )}
      {help.relatedTo && (
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground/80">Related: </span>
          {help.relatedTo}
        </p>
      )}
    </div>
  );
}

export interface FieldHelpPopoverProps {
  /** Entity registry key (e.g. "deals", "contacts"). */
  entity: string;
  /** Field id — must match the entity's field config key. */
  fieldId: string;
  /** Popover side. Defaults to "top". */
  side?: "top" | "bottom" | "left" | "right";
  align?: "start" | "center" | "end";
  className?: string;
}

/**
 * Single-field help "?" — for table headers and the inline editor panel.
 * Returns null when the field has no help defined.
 */
export function FieldHelpPopover({
  entity,
  fieldId,
  side = "top",
  align = "center",
  className,
}: FieldHelpPopoverProps) {
  const help = getFieldHelp(entity, fieldId);
  if (!help) return null;

  return (
    <ResponsivePopover forcePopover
      content={<FieldHelpBody help={help} />}
      title={help.title}
      popoverSide={side}
      align={align}
      className="w-72"
      showCloseButton={false}
      asChild
    >
      <HelpIconButton ariaLabel={`What is ${help.title}?`} className={className} />
    </ResponsivePopover>
  );
}

export interface CardFieldHelpPopoverProps {
  entity: string;
  /** Field ids shown on the card — only those with help are listed. */
  fieldIds: string[];
  side?: "top" | "bottom" | "left" | "right";
  align?: "start" | "center" | "end";
  className?: string;
}

/**
 * Card-level help "?" — a single icon (e.g. top-right of a card) that lists help
 * for the card's shown fields. Progressive disclosure: one unobtrusive icon
 * instead of one per field. Returns null when none of the fields have help.
 */
export function CardFieldHelpPopover({
  entity,
  fieldIds,
  side = "bottom",
  align = "end",
  className,
}: CardFieldHelpPopoverProps) {
  const all = getEntityFieldHelp(entity);
  const entries = fieldIds
    .map((id) => all[id])
    .filter((h): h is FieldHelp => Boolean(h));

  if (entries.length === 0) return null;

  return (
    <ResponsivePopover forcePopover
      content={
        <div className="space-y-3">
          {entries.map((help) => (
            <FieldHelpBody key={help.title} help={help} />
          ))}
        </div>
      }
      title="About these fields"
      popoverSide={side}
      align={align}
      className="w-80 max-h-80 overflow-y-auto"
      showCloseButton={false}
      asChild
    >
      <HelpIconButton ariaLabel="About these fields" className={className} />
    </ResponsivePopover>
  );
}
